import { AsyncPipe } from '@angular/common';
import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  input,
  Renderer2,
  Signal,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ElementHostComponent } from '@components/element-widgets/element-host/element-host.component';
import { ElementHostOptions, TableType } from '@components/element-widgets/element-host/element-host.options';
import { DokaComponent } from '@components/fondsportrait/doka/doka.component';
import { fundsSearchParamName } from '@components/fondsportrait/selected-fund';
import { TranslocoModule } from '@ngneat/transloco';
import type SdDrawer from '@solid-design-system/components/dist-versioned/components/drawer/drawer';
import { CustomEvents } from '@utils/events/custom-events';
import { EventContext } from '@utils/events/event-context';
import { LoadingIndicatorDirective } from 'src/app/common/directives/loading-indicator.directive';
import { LOG } from 'src/app/common/util/log';
import { ElementPipe } from 'src/app/element-handling/element.pipe';
import { ProvideElementsDirective } from 'src/app/element-handling/provide-elements.directive';
import { Extern } from 'src/app/extern';
import { CUID } from 'src/environments/cuids';

@Component({
  selector: 'wct-advanced-fond-list-drawer',
  standalone: true,
  imports: [
    TranslocoModule,
    ElementHostComponent,
    DokaComponent,
    ProvideElementsDirective,
    LoadingIndicatorDirective,
    ElementPipe,
    AsyncPipe,
  ],
  templateUrl: './advanced-fond-list-drawer.component.html',
  styleUrl: './advanced-fond-list-drawer.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [EventContext],
})
export class AdvancedFondListDrawerComponent {
  private readonly eventContext = inject(EventContext);
  private readonly renderer = inject(Renderer2);
  private readonly extern = inject(Extern);

  public readonly showPublicDocuments = input<boolean>(true);
  public readonly redirectUrl = input<string>('');
  public readonly showUserQuicklinks = input<boolean>(false);

  @ViewChild('quicklinks', { read: ElementRef, static: true })
  protected quicklinksContainer: ElementRef<HTMLElement>;
  @ViewChild('subscriptionList', { read: ElementRef, static: true })
  protected subscriptionListContainer: ElementRef<HTMLElement>;
  @ViewChild('drawer', { read: ElementRef, static: true })
  protected drawer: ElementRef<SdDrawer>;
  @ViewChild('documents', { read: ProvideElementsDirective, static: true })
  protected documents: ProvideElementsDirective;
  @ViewChild('doka', { read: DokaComponent, static: true })
  protected dokaHost: DokaComponent;

  protected readonly tableOptions: ElementHostOptions = {
    tableType: TableType.TIGHT,
  };

  protected readonly loading: WritableSignal<boolean> = signal(false);

  protected readonly fondsportraitUrl: Signal<string | undefined> = computed(() => {
    return this.funds()?.length > 0 ? `${this.redirectUrl()}?${fundsSearchParamName}=${this.funds()}` : undefined;
  });

  protected readonly funds: WritableSignal<string> = signal(undefined);

  protected readonly dokaVisible: WritableSignal<boolean> = signal(false);

  protected readonly dokaOptions: Signal<Record<string, string>> = computed(() => {
    const funds: string = this.funds();

    return funds && funds.length > 0
      ? {
          ...this._dokaOptions,
          'show-only-public-documents': this.showPublicDocuments() + '',
          'latest-documents': this.showPublicDocuments() ? 'true' : 'false',
          'fund-number': funds,
        }
      : this._dokaOptions;
  });

  private readonly _dokaOptions: Record<string, string> = {
    'hide-search': 'true',
    'hide-filters': 'true',
    'hide-document-type-filter': 'true',
    'hide-format-filter': 'true',
    'hide-effective-date-filter': 'true',
    'hide-received-date-filter': 'true',
    'hide-source-column': 'true',
    'hide-received-date-column': 'true',
    'hide-effective-date-column': 'true',
    'hide-format-column': 'true',
    'hide-user-public-filter': 'true',
  };

  protected readonly CUID = CUID.advancedFondListDrawer;
  protected readonly realestateFundstypeElement: string = 'IstSachwertfonds';

  constructor() {
    this.eventContext
      .getEvent(CustomEvents.ElementsLoaded)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.loading.set(false));
  }

  public show(funds: string): Promise<void> {
    if (funds !== this.funds()) {
      this.loading.set(true);

      this.funds.set(funds);
      this.documents.clear();

      this.placeExternal(funds);
    }

    return this.drawer.nativeElement.show();
  }

  protected interceptRedirect(event: MouseEvent): void {
    if (this.redirectUrl().length > 0 || !this.funds()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const searhParams: URLSearchParams = new URLSearchParams(window.location.search);
    searhParams.set(fundsSearchParamName, this.funds());

    window.location.search = searhParams.toString();
  }

  private placeExternal(funds: string): void {
    try {
      while (this.quicklinksContainer.nativeElement.lastChild) {
        this.renderer.removeChild(
          this.quicklinksContainer.nativeElement,
          this.quicklinksContainer.nativeElement.lastChild
        );
      }

      while (this.subscriptionListContainer.nativeElement.lastChild) {
        this.renderer.removeChild(
          this.subscriptionListContainer.nativeElement,
          this.subscriptionListContainer.nativeElement.lastChild
        );
      }

      if (this.showUserQuicklinks()) {
        this.extern.placeFpSidepanelCreateSubscriptionButton(this.quicklinksContainer.nativeElement, funds);
        this.extern.placeFpSidepanelSubscribedDocumentsWidget(this.subscriptionListContainer.nativeElement, funds);
      }
    } catch (e) {
      LOG.error(e);
    }
  }
}
